import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function isDevMode(): boolean {
  return process.env.NODE_ENV === 'development';
}

export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDateTime(d);
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatCurrency(amount: number | null | undefined): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

export function formatPercentage(value: number, decimals = 0): string {
  return `${value.toFixed(decimals)}%`;
}

export function isProdMode(): boolean {
  return process.env.NODE_ENV === 'production';
}

// ─── Status / Priority helpers ────────────────────────────────────────────────

type BadgeColor = 'gray' | 'cyan' | 'yellow' | 'green';

export function getStatusColor(status: string): BadgeColor {
  const map: Record<string, BadgeColor> = {
    open: 'gray', new: 'gray', draft: 'gray',
    in_progress: 'cyan', assigned: 'cyan', scheduled: 'cyan',
    pending: 'yellow', pending_approval: 'yellow', on_hold: 'yellow', investigating: 'yellow',
    completed: 'green', closed: 'green', resolved: 'green', approved: 'green',
  };
  return map[status?.toLowerCase()] ?? 'gray';
}

export function getStatusLabel(status: string): string {
  if (!status) return '—';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getPriorityColor(priority: string): BadgeColor {
  const map: Record<string, BadgeColor> = {
    urgent: 'yellow', high: 'yellow', medium: 'cyan', low: 'gray',
  };
  return map[priority?.toLowerCase()] ?? 'gray';
}

export function getPriorityLabel(priority: string): string {
  if (!priority) return '—';
  return priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
}

export function getCategoryIcon(category: string | null | undefined) {
  // Returns null — callers should fall back to a default icon if needed.
  // Category-specific icons can be wired here as the icon system is established.
  return null;
}
